'use client';
import React, { useEffect, useRef, useState } from 'react';

const testimonials = [
  {
    id: 1,
    name: 'Priya Sharma',
    role: 'Smile Design Patient',
    rating: 5,
    text: "I had been self-conscious about my smile for years. Dr. Pal\'s smile design completely transformed my confidence. The digital preview was amazing — I could see my new smile before the treatment even started!",
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80',
    avatarAlt: 'Priya Sharma, satisfied patient at DrPal Dentistry',
  },
  {
    id: 2,
    name: 'Rajesh Mehta',
    role: 'Full Mouth Implant Patient',
    rating: 5,
    text: "After losing multiple teeth, I was hesitant about implants. Dr. Abhishek explained everything patiently and the procedure was completely pain-free. My new implants feel and look exactly like natural teeth!",
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
    avatarAlt: 'Rajesh Mehta, dental implant patient at DrPal Dentistry',
  },
  {
    id: 3,
    name: 'Ananya Krishnan',
    role: 'Veneers Patient',
    rating: 5,
    text: "The porcelain veneers Dr. Pal crafted for me are absolutely stunning. He took so much care to match the shape and shade perfectly. My teeth look naturally beautiful — everyone keeps complimenting my smile!",
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80',
    avatarAlt: 'Ananya Krishnan, veneers patient at DrPal Dentistry',
  },
  {
    id: 4,
    name: 'Vikram Nair',
    role: 'Full Mouth Rehabilitation',
    rating: 5,
    text: "I had severe dental issues that affected my eating and confidence. Dr. Pal's full mouth rehabilitation plan was comprehensive and the results are life-changing. The clinic is modern and the staff is incredibly warm.",
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80',
    avatarAlt: 'Vikram Nair, full mouth rehabilitation patient at DrPal Dentistry',
  },
  {
    id: 5,
    name: 'Meera Patel',
    role: 'Cosmetic Dentistry Patient',
    rating: 5,
    text: "Dr. Pal is not just a dentist — he\'s an artist. His attention to detail and genuine care for patients is unmatched. I was nervous about my first visit but felt completely at ease. I can\'t stop smiling!",
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&q=80',
    avatarAlt: 'Meera Patel, cosmetic dentistry patient at DrPal Dentistry',
  },
];

export default function Testimonials() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={ref} className="bg-secondary py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div
              className={`inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 mb-6 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <span className="font-inter text-xs font-semibold text-dark/60 uppercase tracking-widest">Testimonials</span>
            </div>
            <h2
              className={`font-unbounded font-black text-dark text-3xl lg:text-4xl xl:text-5xl leading-[1.08] transition-all duration-600 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              What Our
              <br />
              Patients Say
            </h2>
          </div>
          <div
            className={`flex items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-soft transition-all duration-500 delay-200 ${
              visible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span className="font-unbounded font-black text-dark text-3xl">4.9</span>
            <div>
              <div className="flex gap-0.5 mb-1">
                {[1, 2, 3, 4, 5]?.map((s) => (
                  <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p className="font-inter text-xs text-dark/45">Average Rating</p>
            </div>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials?.slice(0, 3)?.map((testimonial, i) => (
            <div
              key={testimonial?.id}
              className={`bg-white rounded-3xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: testimonial?.rating })?.map((_, s) => (
                  <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
                <span className="font-inter text-xs text-dark/40 ml-1">{testimonial?.rating}.0</span>
              </div>

              {/* Quote */}
              <p className="font-inter text-dark/65 text-sm leading-relaxed mb-6">
                "{testimonial?.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-secondary">
                <img
                  src={testimonial?.avatar}
                  alt={testimonial?.avatarAlt}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-unbounded font-bold text-dark text-xs">{testimonial?.name}</p>
                  <p className="font-inter text-xs text-dark/45 mt-0.5">{testimonial?.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Second row - 2 cards centered */}
        <div className="grid md:grid-cols-2 gap-4 mt-4 lg:max-w-2xl lg:mx-auto">
          {testimonials?.slice(3, 5)?.map((testimonial, i) => (
            <div
              key={testimonial?.id}
              className={`bg-white rounded-3xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${(i + 3) * 100}ms` }}
            >
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: testimonial?.rating })?.map((_, s) => (
                  <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
                <span className="font-inter text-xs text-dark/40 ml-1">{testimonial?.rating}.0</span>
              </div>
              <p className="font-inter text-dark/65 text-sm leading-relaxed mb-6">
                "{testimonial?.text}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-secondary">
                <img
                  src={testimonial?.avatar}
                  alt={testimonial?.avatarAlt}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-unbounded font-bold text-dark text-xs">{testimonial?.name}</p>
                  <p className="font-inter text-xs text-dark/45 mt-0.5">{testimonial?.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div
          className={`mt-10 bg-primary rounded-3xl p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-600 delay-500 shadow-teal ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <p className="font-inter text-xs font-semibold text-dark/50 uppercase tracking-widest mb-2">
              Smiles That Speak for Themselves
            </p>
            <h3 className="font-unbounded font-black text-dark text-2xl lg:text-3xl leading-tight">
              Ready for Your
              <br />
              Dream Smile?
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-dark text-white font-inter font-semibold text-sm px-8 py-4 rounded-full hover:bg-dark-light transition-all duration-200 hover:shadow-card-hover"
            >
              Book Appointment
            </button>
            <a
              href="tel:+91"
              className="bg-white text-dark font-inter font-semibold text-sm px-8 py-4 rounded-full hover:bg-secondary transition-all duration-200 text-center"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
